import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { alpha } from '@mui/material/styles';
import { explainProjectDag } from '../../Utils/actionsApi';

export function DagHelpExplanation({ result, t, onPrepareCorrection }) {
  const [dismissed, setDismissed] = useState(false);
  const [correctionError, setCorrectionError] = useState(false);
  const [showChecks, setShowChecks] = useState(false);
  const correction = result.corrections?.[0];
  const changeRequested = correction?.intent === 'change';
  return <Stack spacing={2}>
    <Typography sx={(theme) => ({ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      p: 2, borderRadius: '14px', lineHeight: 1.7,
      bgcolor: alpha(theme.palette.primary.main, 0.045),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` })}>{result.summary}</Typography>
    {result.certainty && <Box>
      <Typography variant="caption" color="text.secondary">
        {t(`dag.help.certainty_${result.certainty}`)}
      </Typography>
      {result.checks?.length > 0 && <>
        <Button size="small" onClick={() => setShowChecks(current => !current)} aria-expanded={showChecks}
          sx={{ ml: 1 }}>{t(showChecks ? 'dag.help.hide_checks' : 'dag.help.show_checks')}</Button>
        {showChecks && <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 1 }}>
          {result.checks.map((check, index) => <Typography component="li" variant="caption" key={index}>
            {t(`dag.help.check_${check.kind}`)}
            {result.labels?.[check.node_key] ? ` · ${result.labels[check.node_key]}` : ''}
            {check.timestamp && !Number.isNaN(Date.parse(check.timestamp))
              ? ` · ${new Date(check.timestamp).toLocaleString()}` : ''}
          </Typography>)}
        </Stack>}
      </>}
    </Box>}
    {result.steps?.length > 0 && <Stack component="ol" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {result.steps.map((step, index) => <Box component="li" key={index} sx={(theme) => ({
        display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        bgcolor: 'background.paper', boxShadow: '0 2px 6px rgba(28, 28, 65, 0.03)',
      })}>
        <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, flexShrink: 0, borderRadius: '10px', bgcolor: 'action.selected',
          color: 'primary.main', fontSize: '0.8rem', fontWeight: 700 }}>{index + 1}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, overflowWrap: 'anywhere' }}>
            {step.title || t('dag.help.step', { number: index + 1 })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{step.explanation}</Typography>
        </Box>
      </Box>)}
    </Stack>}
    {correction && onPrepareCorrection && !dismissed && <Box sx={{ p: 2, border: '1px solid',
      borderColor: 'primary.main', borderRadius: '14px' }}>
      <Typography variant="subtitle2">{correction.title}</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{correction.description}</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>{t('dag.help.correction_review')}</Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" onClick={() => {
          if (onPrepareCorrection(correction) === false) setCorrectionError(true);
          else setDismissed(true);
        }}>{t(changeRequested ? 'dag.help.prepare_change' : 'dag.help.prepare_correction')}</Button>
        <Button onClick={() => setDismissed(true)}>{t('dag.help.later')}</Button>
      </Stack>
      {correctionError && <Alert severity="warning" sx={{ mt: 1 }}>{t('dag.help.correction_unavailable')}</Alert>}
    </Box>}
  </Stack>;
}

const explanationText = (result) => [result.summary, ...result.steps.map((step) => [
  step.title || step.node_keys.map((key) => result.labels?.[key]).filter(Boolean).join(' → '), step.explanation,
].filter(Boolean).join(': ')),
  ...(result.limitations || [])].join('\n').slice(0, 2000);

export default function DagHelpConversation({ caseid, locale, nodeKeys, explanation, open, t, onPrepareCorrection }) {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');
  const request = useRef(null);
  const scrollArea = useRef(null);
  const followReplies = useRef(false);
  const input = useRef(null);

  useEffect(() => {
    if (!open) { request.current?.abort(); request.current = null; }
    return () => { request.current?.abort(); request.current = null; };
  }, [open]);

  useEffect(() => {
    const area = scrollArea.current;
    if (area && followReplies.current) area.scrollTop = area.scrollHeight;
  }, [messages, pending, error]);

  const send = async (event) => {
    event.preventDefault();
    const text = question.trim();
    if (!text || text.length > 2000 || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    followReplies.current = true;
    setPending(text);
    setQuestion('');
    setError('');
    const history = [{ role: 'assistant', text: explanationText(explanation) },
      ...messages.map((message) => ({ role: message.role, text: message.text.slice(0, 2000) }))].slice(-8);
    const timeout = setTimeout(() => controller.abort(), 120000);
    try {
      const result = await explainProjectDag(caseid, {
        node_keys: nodeKeys, language: locale || 'en', question: text, history,
      }, controller.signal);
      if (request.current !== controller) return;
      setMessages((current) => [...current, { role: 'user', text },
        { role: 'assistant', text: explanationText(result), result }]);
    } catch (_) {
      if (request.current !== controller) return;
      setQuestion(text);
      setError(t('dag.help.chat_error'));
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) {
        request.current = null;
        setPending('');
        // Wait until the input is enabled again; focus without scrolling the graph.
        requestAnimationFrame(() => input.current?.focus({ preventScroll: true }));
      }
    }
  };

  return <Stack sx={{ flex: 1, minHeight: 0 }} spacing={2}>
    <Box ref={scrollArea} role="log" tabIndex={0} aria-label={t('dag.help.conversation')} aria-live="polite"
      onScroll={(event) => {
        const area = event.currentTarget;
        followReplies.current = area.scrollHeight - area.scrollTop - area.clientHeight < 48;
      }}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.75, pb: 0.5, scrollbarGutter: 'stable',
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2, borderRadius: 1 } }}>
      <DagHelpExplanation result={explanation} t={t} onPrepareCorrection={!messages.length && !pending ? onPrepareCorrection : undefined} />
      {messages.map((message, index) => <Box key={index} sx={{ mt: 2, p: 1.5, borderRadius: 2,
        bgcolor: message.role === 'user' ? 'action.hover' : 'transparent', ml: message.role === 'user' ? 3 : 0 }}>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
          {t(message.role === 'user' ? 'dag.help.you' : 'dag.help.answer')}
        </Typography>
        {message.result ? <DagHelpExplanation result={message.result} t={t}
          onPrepareCorrection={index === messages.length - 1 && !pending ? onPrepareCorrection : undefined} />
          : <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.text}</Typography>}
      </Box>)}
      {pending && <>
        <Box sx={{ mt: 2, ml: 3, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{pending}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} role="status" sx={{ mt: 2 }}>
          <CircularProgress size={16} /><Typography variant="body2">{t('dag.help.answering')}</Typography>
        </Stack>
      </>}
    </Box>
    {error && <Alert severity="warning">{error}</Alert>}
    <Box component="form" onSubmit={send} sx={{ pt: 0.75 }}>
      <TextField inputRef={input} fullWidth multiline minRows={1} maxRows={4} size="small"
        label={t(onPrepareCorrection ? 'dag.help.question_or_change' : 'dag.help.question')}
        placeholder={t(onPrepareCorrection ? 'dag.help.change_placeholder' : 'dag.help.question_placeholder')}
        value={question} disabled={Boolean(pending)} inputProps={{ maxLength: 2000 }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: 'background.paper',
          boxShadow: '0 3px 12px rgba(28, 28, 65, 0.05)' } }}
        onChange={(event) => setQuestion(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) send(event);
        }}
        InputProps={{ endAdornment: <InputAdornment position="end">
          <IconButton type="submit" aria-label={t('dag.help.send')} disabled={!question.trim() || Boolean(pending)} color="primary"
            sx={{ borderRadius: '10px', bgcolor: 'primary.main', color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.hover' } }}>
            <SendRoundedIcon fontSize="small" />
          </IconButton>
        </InputAdornment> }}
      />
    </Box>
  </Stack>;
}
